import React, { useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  type MarketingRouterInternal,
  type MarketingRouterOverlapSibling,
  type MarketingRouterTreatmentType,
  checkMarketingRouter,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';
import { getRegions } from '@luxuryescapes/lib-regions';

import CopyableId from '~/components/Common/Elements/CopyableId';
import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  useCreateMarketingRouter,
  useMarketingRouterRules,
  useUpdateMarketingRouter,
} from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type {
  MarketingRouterCreatePayload,
  MarketingRouterInternalQuery,
  MarketingRouterUpdatePayload,
} from '~/services/PromoService';

import ApiError from '~/types/ApiError';
import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { normalizeToISODate } from '~/utils/dateFormatter';

import MarketingRouterAdUrlField from './MarketingRouterAdUrlField';
import MarketingRouterMatchTagsField from './MarketingRouterMatchTagsField';
import MarketingRouterMetricsField from './MarketingRouterMetricsField';
import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import MarketingRouterVariantCard from './MarketingRouterVariantCard';
import { getRuleChanges } from './marketingRouterChanges';
import { toMatchLists, toMatchValues } from './marketingRouterMatch';
import { getMatchSummary } from './marketingRouterMatchSummary';
import {
  getConflictingRuleEditPath,
  getMatchSnapshot,
  getOverlapBullets,
  getOverlapConflictKey,
  getOverlapGate,
  getOverlapGateMessage,
  getOverlapWarnings,
  getRuleDefinitionRows,
} from './marketingRouterOverlap';
import { getDuplicateMarketingRouterRuleName } from './marketingRouterRuleDuplication';
import {
  type MarketingRouterTreatmentDraft,
  type MarketingRouterTreatmentFieldName,
  getPersistedTreatmentValue,
  normalizeTreatmentForType,
} from './marketingRouterTreatments';

type RouteParams = { marketingRouterId?: string };
type Treatment = MarketingRouterTreatmentDraft;
type Variant = Omit<MarketingRouterRule['variants'][number], 'treatments'> & { treatments?: Array<Treatment> };
type RuleFormData = Omit<MarketingRouterRule, 'variants'> & { variants: Array<Variant> };
const DEFAULT_TREATMENT_TYPE: MarketingRouterTreatmentType = 'mlos-enable';
const UTM_MATCH_HELPER_TEXT = 'Matched exactly. Leave empty to match every value.';

function createDefaultTreatment(): Treatment {
  return { type: DEFAULT_TREATMENT_TYPE };
}

function appendTreatment(variant: Variant): Variant {
  const treatments = variant.treatments ?? [];

  return {
    ...variant,
    treatments: [...treatments, createDefaultTreatment()],
  };
}

function omitTreatments(variant: Variant): Variant {
  const { treatments: _treatments, ...variantWithoutTreatments } = variant;
  return variantWithoutTreatments;
}

const emptyRule: RuleFormData = {
  id: '',
  name: '',
  significance_metrics: ['ttv'],
  start_date: '',
  end_date: '',
  match: { utm_source: [], utm_medium: [], utm_campaign: [] },
  variants: [
    {
      variant_id: '',
      variant_name: '',
      weight: 100,
      is_control: true,
    },
  ],
};

function hasAdvancedMatchConditions(match: MarketingRouterRule['match']): boolean {
  return [match.utm_term, match.utm_content, match.url].some((values) => toMatchValues(values).length > 0);
}

// The single boundary a stored rule crosses into form state.
//
// The rule records returned by useMarketingRouterRules carry router-level
// context (marketingRouterId, brand, ruleJson, …). Only the canonical rule
// fields may enter form state — svc-promo rejects anything else on save.
//
// Match fields are read as lists here so the rest of the form can treat them
// as the arrays their types promise; see toMatchLists for the stale shape
// that makes this necessary.
function toRuleFormData(rule: RuleFormData): RuleFormData {
  return {
    id: rule.id,
    name: rule.name,
    significance_metrics: rule.significance_metrics,
    start_date: rule.start_date,
    end_date: rule.end_date,
    match: toMatchLists(rule.match),
    variants: rule.variants,
  };
}

/**
 * A duplicate is the source rule with its identity removed: svc-promo mints a
 * new rule id and new variant ids on save (see prepareRule) whenever those
 * fields are blank, so clearing them here is all the regeneration that is
 * needed. Everything the operator configured — dates, match, metrics,
 * variants, treatments — carries over untouched. The router-level region the
 * copy is saved under is carried over separately; see routerRegion.
 */
function toDuplicateFormData(rule: RuleFormData, existingNames: Iterable<string>): RuleFormData {
  const formData = toRuleFormData(rule);

  return {
    ...formData,
    id: '',
    name: getDuplicateMarketingRouterRuleName(formData.name, existingNames),
    variants: formData.variants.map((variant) => {
      const duplicatedVariant = { ...variant, variant_id: '' };

      return variant.treatments
        ? { ...duplicatedVariant, treatments: variant.treatments.map((treatment) => ({ ...treatment })) }
        : omitTreatments(duplicatedVariant);
    }),
  };
}

function cleanMatchValues(values?: Array<string>): Array<string> {
  return (values ?? []).map((value) => value.trim()).filter(Boolean);
}

function prepareRule(rule: RuleFormData, id: string, generateVariantIds = true): MarketingRouterRule {
  return {
    ...toRuleFormData(rule),
    id,
    name: rule.name.trim(),
    start_date: normalizeToISODate(rule.start_date),
    end_date: normalizeToISODate(rule.end_date),
    match: {
      utm_source: cleanMatchValues(rule.match.utm_source),
      utm_medium: cleanMatchValues(rule.match.utm_medium),
      utm_campaign: cleanMatchValues(rule.match.utm_campaign),
      ...(cleanMatchValues(rule.match.regions).length ? { regions: cleanMatchValues(rule.match.regions) } : {}),
      ...(cleanMatchValues(rule.match.utm_term).length ? { utm_term: cleanMatchValues(rule.match.utm_term) } : {}),
      ...(cleanMatchValues(rule.match.utm_content).length
        ? { utm_content: cleanMatchValues(rule.match.utm_content) }
        : {}),
      ...(cleanMatchValues(rule.match.url).length ? { url: cleanMatchValues(rule.match.url) } : {}),
    },
    variants: rule.variants.map((variants) => {
      const { treatments, ...variant } = variants;
      const preparedVariant = {
        ...variant,
        variant_id: variant.variant_id || (generateVariantIds ? globalThis.crypto.randomUUID() : ''),
        variant_name: variant.variant_name.trim(),
      };

      return treatments
        ? {
            ...preparedVariant,
            treatments: treatments.map(getPersistedTreatmentValue),
          }
        : preparedVariant;
    }),
  };
}

/**
 * svc-promo's 400s carry the structured check warnings in `result.errors`;
 * the shared request helper only lifts top-level `errors`, so read the
 * response body directly rather than showing an opaque "bad request".
 */
function toSaveErrorMessages(error: unknown): Array<string> {
  if (error instanceof ApiError) {
    const body = error.responseBody as
      | { result?: { errors?: Array<{ code?: string; message?: string }> } }
      | null
      | undefined;
    const responseErrors = body?.result?.errors;
    if (Array.isArray(responseErrors) && responseErrors.length > 0) {
      return responseErrors.map((entry) => entry.message || entry.code || 'Validation error');
    }
  }
  return [error instanceof Error ? error.message : 'Failed to save marketing router rule.'];
}

export default function MarketingRouterRuleFormPage() {
  const navigate = useNavigate();
  const { marketingRouterId } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);
  const existingRule = useMemo(
    () => rules.find((rule) => rule.marketingRouterId === marketingRouterId),
    [marketingRouterId, rules],
  );
  // Duplicating reuses the create route, so the copy is saved by the create
  // path as a rule of its own and the source rule is never written to.
  const duplicateFrom = searchParams.get('duplicateFrom');
  const duplicateSourceRule = useMemo(
    () => (duplicateFrom ? rules.find((candidate) => candidate.marketingRouterId === duplicateFrom) : undefined),
    [duplicateFrom, rules],
  );
  // Every rule the brand has, so a duplicate can pick a name none of them use.
  const ruleNames = useMemo(() => rules.map((candidate) => candidate.name), [rules]);
  // The router-level region this rule is saved under. Editing keeps the
  // router's own region, and duplicating carries the source router's region
  // over so an AU-only rule does not become a copy that targets every region.
  // A plain create has no region control, so it stays unset.
  const routerRegion = (existingRule?.region ??
    duplicateSourceRule?.region ??
    null) as MarketingRouterInternal['region'];
  const createMarketingRouterMutation = useCreateMarketingRouter();
  const updateMarketingRouterMutation = useUpdateMarketingRouter();
  const [rule, setRule] = useState<RuleFormData>(() => {
    if (existingRule) {
      return toRuleFormData(existingRule);
    }

    return duplicateSourceRule ? toDuplicateFormData(duplicateSourceRule, ruleNames) : emptyRule;
  });
  const [isAdvancedMatchVisible, setIsAdvancedMatchVisible] = useState(() =>
    hasAdvancedMatchConditions(existingRule?.match ?? duplicateSourceRule?.match ?? emptyRule.match),
  );
  const [showFieldDescriptions, setShowFieldDescriptions] = useState(false);
  const [errors, setErrors] = useState<Array<string>>([]);
  // Overlaps the operator has confirmed, keyed by the other rule. A saved
  // rule opens with its overlaps already confirmed — saving it was the
  // confirmation — and editing the match re-opens the question.
  const [acknowledgedConflictKeys, setAcknowledgedConflictKeys] = useState<Array<string>>([]);
  const isEditing = Boolean(marketingRouterId);
  const isDuplicating = Boolean(duplicateFrom);
  const isSaving = createMarketingRouterMutation.isPending || updateMarketingRouterMutation.isPending;
  // react-helmet takes a single string child, so the title is built here
  // rather than branching inside the tag.
  const documentTitle = isEditing
    ? 'Marketing / Edit Router Rule'
    : isDuplicating
      ? 'Marketing / Duplicate Router Rule'
      : 'Marketing / Create Router Rule';
  const pageTitle = isEditing
    ? `Edit Marketing Router Rule: ${existingRule?.name ?? marketingRouterId ?? ''}`
    : isDuplicating
      ? 'Duplicate Marketing Router Rule'
      : 'Create Marketing Router Rule';

  useEffect(() => {
    if (existingRule) {
      setRule(toRuleFormData(existingRule));
      setIsAdvancedMatchVisible(hasAdvancedMatchConditions(existingRule.match));
    } else if (duplicateSourceRule) {
      setRule(toDuplicateFormData(duplicateSourceRule, ruleNames));
      setIsAdvancedMatchVisible(hasAdvancedMatchConditions(duplicateSourceRule.match));
    }
  }, [duplicateSourceRule, existingRule, ruleNames]);
  // The summary reports one submit attempt. Editing anything can resolve what
  // it complained about, so it must not outlive the rule it was raised for.
  useEffect(() => {
    setErrors([]);
  }, [rule]);
  const variantsTotal = rule.variants.reduce((total, variants) => total + variants.weight, 0);
  const hasInvalidVariantTotal = variantsTotal !== 100;
  const currentRule = useMemo(() => prepareRule(rule, rule.id || 'new-marketing-router-rule', false), [rule]);
  const regionCodes = useMemo(
    () =>
      getRegions(tenant.brand)
        .map((region) => region.code)
        .sort(),
    [tenant.brand],
  );
  // The brand's other routers, so the pre-save check can flag cross-router
  // overlap with the same warnings the server now enforces on the write path.
  const siblingRouters = useMemo(() => {
    const routersById = new Map<string, MarketingRouterOverlapSibling>();
    for (const record of rules) {
      if (!routersById.has(record.marketingRouterId)) {
        routersById.set(record.marketingRouterId, {
          marketing_router_id: record.marketingRouterId,
          region: record.region as MarketingRouterOverlapSibling['region'],
          start_date: record.routerStartDate,
          end_date: record.routerEndDate,
          rule_json: record.ruleJson as MarketingRouterOverlapSibling['rule_json'],
        });
      }
    }
    return Array.from(routersById.values());
  }, [rules]);
  const currentRuleCheck = useMemo(
    () =>
      checkMarketingRouter(
        {
          rule_json: [currentRule] as MarketingRouterInternal['rule_json'],
          marketing_router_id: existingRule?.marketingRouterId,
          region: routerRegion,
          start_date: currentRule.start_date || null,
          end_date: currentRule.end_date || null,
        },
        { siblingRouters },
      ),
    [currentRule, existingRule, routerRegion, siblingRouters],
  );
  // Overlap findings have their own section under Match; the summary at the
  // bottom keeps everything else.
  const currentRuleErrors = useMemo(
    () =>
      Array.from(
        new Set(
          currentRuleCheck.warnings
            .filter((warning) => warning.level === 'error' && warning.type !== 'overlapping-rule')
            .map((warning) => warning.message),
        ),
      ),
    [currentRuleCheck.warnings],
  );
  // The check is pure and reads only form state, so it re-runs on every edit
  // rather than behind a button: the operator never sees a stale verdict.
  const overlapWarnings = useMemo(() => getOverlapWarnings(currentRuleCheck), [currentRuleCheck]);
  const matchSnapshot = useMemo(() => getMatchSnapshot(currentRule), [currentRule]);
  // Read from the prepared rule so the sentence describes the match as it
  // would be saved, with blank tags already dropped.
  const matchSummary = useMemo(() => getMatchSummary(currentRule.match), [currentRule]);
  const savedRule = useMemo(
    () => (existingRule == null ? null : prepareRule(toRuleFormData(existingRule), existingRule.id, false)),
    [existingRule],
  );
  const savedMatchSnapshot = useMemo(() => (savedRule == null ? null : getMatchSnapshot(savedRule)), [savedRule]);
  // Prepared both sides, so trimming and date normalisation do not read as an
  // edit. A rule that has never been saved is unsaved by definition.
  const hasUnsavedChanges = savedRule == null || JSON.stringify(savedRule) !== JSON.stringify(currentRule);
  const ruleChanges = useMemo(() => getRuleChanges(savedRule, currentRule), [savedRule, currentRule]);
  const overlapKeys = overlapWarnings.map(getOverlapConflictKey).join('|');

  // While the match is the one that was saved, its overlaps are already
  // confirmed; any change to the match clears that and asks again. Ticking or
  // unticking a box afterwards is left alone — this only runs when the match
  // or the set of overlapping rules actually changes.
  useEffect(() => {
    setAcknowledgedConflictKeys(matchSnapshot === savedMatchSnapshot ? overlapKeys.split('|').filter(Boolean) : []);
  }, [matchSnapshot, savedMatchSnapshot, overlapKeys]);
  const overlapGate = getOverlapGate({
    warnings: overlapWarnings,
    acknowledgedKeys: acknowledgedConflictKeys,
  });
  const overlapGateMessage = getOverlapGateMessage(overlapGate);

  const setConflictAcknowledged = (conflictKey: string, acknowledged: boolean) => {
    setAcknowledgedConflictKeys((current) =>
      acknowledged ? Array.from(new Set([...current, conflictKey])) : current.filter((key) => key !== conflictKey),
    );
  };

  const updateMatch = (field: keyof MarketingRouterRule['match'], values: Array<string>) => {
    setRule((currentRule) => ({ ...currentRule, match: { ...currentRule.match, [field]: values } }));
  };

  // An extraction can fill the advanced fields, which are no use to the
  // operator while they are hidden: they would be reviewing a rule with
  // conditions they cannot see. Revealing them keeps every filled value
  // editable, as AC2 requires.
  const applyExtractedMatch = (match: MarketingRouterRule['match']) => {
    setRule((currentRule) => ({ ...currentRule, match }));

    if (hasAdvancedMatchConditions(match)) {
      setIsAdvancedMatchVisible(true);
    }
  };

  const updateAdvancedMatchVisibility = (isVisible: boolean) => {
    setIsAdvancedMatchVisible(isVisible);

    if (!isVisible) {
      setRule((currentRule) => {
        const { utm_term: _utmTerm, utm_content: _utmContent, url: _url, ...match } = currentRule.match;

        return { ...currentRule, match };
      });
    }
  };

  const updateVariant = (index: number, field: keyof Variant, value: string) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variants, variantsIndex) =>
        variantsIndex === index ? { ...variants, [field]: field === 'weight' ? Number(value) : value } : variants,
      ),
    }));
  };

  const addVariant = () => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: [
        ...currentRule.variants,
        {
          variant_id: '',
          variant_name: '',
          weight: 0,
          is_control: false,
        },
      ],
    }));
  };

  const addTreatment = (variantIndex: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) =>
        currentVariantIndex === variantIndex ? appendTreatment(variant) : variant,
      ),
    }));
  };

  const updateTreatmentType = (variantIndex: number, treatmentIndex: number, type: MarketingRouterTreatmentType) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) =>
        currentVariantIndex === variantIndex
          ? {
              ...variant,
              treatments: variant.treatments?.map((treatment, currentTreatmentIndex) =>
                currentTreatmentIndex === treatmentIndex ? normalizeTreatmentForType(treatment, type) : treatment,
              ),
            }
          : variant,
      ),
    }));
  };

  const updateTreatmentField = (
    variantIndex: number,
    treatmentIndex: number,
    field: MarketingRouterTreatmentFieldName,
    value: string,
  ) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) =>
        currentVariantIndex === variantIndex
          ? {
              ...variant,
              treatments: variant.treatments?.map((treatment, currentTreatmentIndex) =>
                currentTreatmentIndex === treatmentIndex ? { ...treatment, [field]: value } : treatment,
              ),
            }
          : variant,
      ),
    }));
  };

  const removeTreatment = (variantIndex: number, treatmentIndex: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) => {
        if (currentVariantIndex !== variantIndex) {
          return variant;
        }

        const treatments = variant.treatments?.filter((_, index) => index !== treatmentIndex) ?? [];

        return treatments.length ? { ...variant, treatments } : omitTreatments(variant);
      }),
    }));
  };

  const removeVariant = (index: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.filter((_, variantsIndex) => variantsIndex !== index),
    }));
  };

  const setControlVariant = (index: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variants, variantsIndex) => ({
        ...variants,
        is_control: variantsIndex === index,
      })),
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors([]);
    const ruleId = rule.id || globalThis.crypto.randomUUID();

    const preparedRule = prepareRule(rule, ruleId);
    // Check exactly the payload the server will validate: the whole spliced
    // rule list on update, against the brand's other routers.
    const ruleJson =
      isEditing && existingRule
        ? existingRule.ruleJson.map((candidate) => ({
            ...candidate,
            start_date:
              candidate.id === preparedRule.id ? preparedRule.start_date : normalizeToISODate(candidate.start_date),
            end_date: candidate.id === preparedRule.id ? preparedRule.end_date : normalizeToISODate(candidate.end_date),
            ...(candidate.id === preparedRule.id ? preparedRule : {}),
          }))
        : [preparedRule];
    const preparedRuleCheck = checkMarketingRouter(
      {
        rule_json: ruleJson as MarketingRouterInternal['rule_json'],
        marketing_router_id: existingRule?.marketingRouterId,
        region: routerRegion,
        start_date: preparedRule.start_date,
        end_date: preparedRule.end_date,
      },
      { siblingRouters },
    );

    // Overlap findings are owned by the Overlaps section, which is always
    // current; the summary carries only what has no home of its own, and the
    // gate contributes one line pointing at that section.
    const blockingMessages = Array.from(
      new Set(
        preparedRuleCheck.warnings
          .filter((warning) => warning.level === 'error' && warning.type !== 'overlapping-rule')
          .map((warning) => warning.message ?? warning.type),
      ),
    );
    if (overlapGateMessage) {
      blockingMessages.push(overlapGateMessage);
    }
    if (blockingMessages.length > 0) {
      setErrors(blockingMessages);
      return;
    }

    try {
      if (isEditing && existingRule) {
        await updateMarketingRouterMutation.mutateAsync({
          marketingRouterId: existingRule.marketingRouterId,
          brand: existingRule.brand as MarketingRouterCreatePayload['brand'],
          region: existingRule.region as MarketingRouterCreatePayload['region'],
          startDate: preparedRule.start_date,
          endDate: preparedRule.end_date,
          // Form state carries regions as plain strings; the restricted tag
          // options and svc-promo's lib-regions validation bound the values.
          ruleJson: ruleJson as MarketingRouterUpdatePayload['ruleJson'],
        });
      } else {
        await createMarketingRouterMutation.mutateAsync({
          brand: tenant.brand as MarketingRouterCreatePayload['brand'],
          // Omitted rather than sent as null: the create body accepts no
          // region at all for a router that targets every region.
          ...(routerRegion ? { region: routerRegion } : {}),
          startDate: preparedRule.start_date,
          endDate: preparedRule.end_date,
          ruleJson: ruleJson as MarketingRouterCreatePayload['ruleJson'],
        });
      }
      navigate('/marketing/marketing-router-rules');
    } catch (mutationError) {
      setErrors(toSaveErrorMessages(mutationError));
    }
  };

  if ((isEditing || isDuplicating) && rulesQuery.isLoading) {
    return (
      <Container>
        <Typography color="text.secondary">Loading marketing router rule…</Typography>
      </Container>
    );
  }

  if ((isEditing || isDuplicating) && rulesQuery.isError) {
    return (
      <Container>
        <Alert severity="error">
          {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router rule.'}
        </Alert>
      </Container>
    );
  }

  if (isDuplicating && !duplicateSourceRule) {
    return (
      <Container>
        <Alert severity="error">Marketing router “{duplicateFrom}” was not found, so it cannot be duplicated.</Alert>
        <Button component={Link} to="/marketing/marketing-router-rules" variant="text" sx={{ mt: 2 }}>
          Back to rules
        </Button>
      </Container>
    );
  }

  if (isEditing && !existingRule) {
    return (
      <Container>
        <Alert severity="error">Marketing Router Rule “{marketingRouterId}” was not found.</Alert>
        <Button component={Link} to="/marketing/marketing-router-rules" variant="text" sx={{ mt: 2 }}>
          Back to rules
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>
      <Container>
        <PageHeader title={pageTitle}>
          <Tooltip title={showFieldDescriptions ? 'Hide field descriptions' : 'Show field descriptions'}>
            <IconButton
              color={showFieldDescriptions ? 'primary' : 'default'}
              aria-label={showFieldDescriptions ? 'Hide field descriptions' : 'Show field descriptions'}
              aria-pressed={showFieldDescriptions}
              onClick={() => setShowFieldDescriptions((current) => !current)}
              size="small"
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </PageHeader>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            {errors.length ? (
              <Alert severity="error">
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {errors.map((error, index) => (
                    <Box component="li" key={`${error}-${index}`}>
                      {error}
                    </Box>
                  ))}
                </Box>
              </Alert>
            ) : null}
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: 'center' }}>
                <Typography variant="h2">
                  <MarketingRouterTermLabel term="rule">Rule details</MarketingRouterTermLabel>
                </Typography>
                {existingRule ? (
                  <CopyableId
                    name="Marketing router ID"
                    value={existingRule.marketingRouterId}
                    showName
                    color="text.secondary"
                  />
                ) : null}
              </Stack>
              <TextField
                label="Rule name"
                required
                fullWidth
                value={rule.name}
                onChange={(event) => setRule((current) => ({ ...current, name: event.target.value }))}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start date"
                  required
                  type="date"
                  fullWidth
                  value={rule.start_date}
                  onChange={(event) => setRule((current) => ({ ...current, start_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date"
                  required
                  type="date"
                  fullWidth
                  value={rule.end_date}
                  onChange={(event) => setRule((current) => ({ ...current, end_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h2">Match</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAdvancedMatchVisible}
                      onChange={(_, checked) => updateAdvancedMatchVisibility(checked)}
                    />
                  }
                  label="Advanced"
                />
              </Stack>
              <MarketingRouterAdUrlField match={rule.match} onChange={applyExtractedMatch} regionCodes={regionCodes} />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <MarketingRouterMatchTagsField
                  label="Regions"
                  values={rule.match.regions ?? []}
                  onChange={(values) => updateMatch('regions', values)}
                  options={regionCodes}
                  helperText="Matched exactly. Leave empty to target every region."
                />
                <MarketingRouterMatchTagsField
                  label="UTM source"
                  values={rule.match.utm_source ?? []}
                  onChange={(values) => updateMatch('utm_source', values)}
                  helperText={UTM_MATCH_HELPER_TEXT}
                />
                <MarketingRouterMatchTagsField
                  label="UTM medium"
                  values={rule.match.utm_medium ?? []}
                  onChange={(values) => updateMatch('utm_medium', values)}
                  helperText={UTM_MATCH_HELPER_TEXT}
                />
                <MarketingRouterMatchTagsField
                  label="UTM campaign"
                  values={rule.match.utm_campaign ?? []}
                  onChange={(values) => updateMatch('utm_campaign', values)}
                  helperText={UTM_MATCH_HELPER_TEXT}
                />
              </Stack>
              {isAdvancedMatchVisible ? (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <MarketingRouterMatchTagsField
                    label="UTM term"
                    values={rule.match.utm_term ?? []}
                    onChange={(values) => updateMatch('utm_term', values)}
                  />
                  <MarketingRouterMatchTagsField
                    label="UTM content"
                    values={rule.match.utm_content ?? []}
                    onChange={(values) => updateMatch('utm_content', values)}
                  />
                  <MarketingRouterMatchTagsField
                    label="URL"
                    supportsWildcards
                    values={rule.match.url ?? []}
                    onChange={(values) => updateMatch('url', values)}
                  />
                </Stack>
              ) : null}
              {matchSummary != null ? <Alert severity="info">{matchSummary}</Alert> : null}
            </Stack>
            <Stack spacing={2}>
              <Typography variant="h3">Overlaps</Typography>
              {overlapWarnings.length === 0 ? (
                <Alert severity="success">No other rule serves this traffic in this date window.</Alert>
              ) : null}
              {overlapWarnings.map((warning) => {
                const conflictKey = getOverlapConflictKey(warning);
                const isBlocking = warning.level === 'error';
                const isAcknowledged = acknowledgedConflictKeys.includes(conflictKey);
                const otherRule = rules.find((candidate) => candidate.id === warning.conflictingRuleId);
                const definitionRows = getRuleDefinitionRows(otherRule);
                const hasDefinition = definitionRows.length > 0;

                return (
                  <Alert key={conflictKey} severity={isBlocking ? 'error' : 'warning'}>
                    <AlertTitle>
                      {isBlocking ? 'Conflict — this rule cannot be saved' : 'Warning — you can save after confirming'}
                    </AlertTitle>
                    <Stack spacing={1.5}>
                      <Typography variant="body2">
                        This rule overlaps with{' '}
                        <MuiLink
                          href={getConflictingRuleEditPath(warning)}
                          target="_blank"
                          rel="noreferrer"
                          underline="hover"
                        >
                          {warning.conflictingRuleName}
                        </MuiLink>
                        .
                      </Typography>
                      {/* A grid, not two stacked columns: the bullets have to
                          start level with the table itself, not with the
                          caption sitting above it. */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: hasDefinition ? 'auto minmax(0, 1fr)' : '1fr' },
                          columnGap: 2,
                          rowGap: 1,
                          alignItems: 'start',
                        }}
                      >
                        {hasDefinition ? (
                          <>
                            <Typography variant="body2" sx={{ gridColumn: '1', gridRow: '1' }}>
                              Their rule is defined as:
                            </Typography>
                            <TableContainer
                              sx={{
                                gridColumn: '1',
                                gridRow: '2',
                                border: 1,
                                borderColor: 'divider',
                                width: 'fit-content',
                              }}
                            >
                              <Table size="small">
                                <TableBody>
                                  {definitionRows.map((row) => (
                                    <TableRow key={row.label}>
                                      <TableCell sx={{ width: 160, fontWeight: 'medium' }}>{row.label}</TableCell>
                                      <TableCell>{row.value}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : null}
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            pl: 2.5,
                            gridColumn: hasDefinition ? { xs: '1', md: '2' } : '1',
                            gridRow: hasDefinition ? { xs: '3', md: '2' } : '1',
                          }}
                        >
                          {getOverlapBullets(warning, { thisRule: currentRule, otherRule }).map((bullet) => (
                            <Box component="li" key={bullet}>
                              <Typography variant="body2">{bullet}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      {isBlocking ? null : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isAcknowledged}
                              onChange={(_, checked) => setConflictAcknowledged(conflictKey, checked)}
                            />
                          }
                          label="I understand this overlap — save anyway"
                        />
                      )}
                    </Stack>
                  </Alert>
                );
              })}
            </Stack>
            <Stack spacing={2}>
              <Typography variant="h2">
                <MarketingRouterTermLabel term="metrics">Metrics</MarketingRouterTermLabel>
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Select the metrics used to measure statistical significance. The first metric is the{' '}
                <MarketingRouterTermLabel term="primaryMetric">primary metric</MarketingRouterTermLabel> and drives the
                recommendation in Results.
              </Typography>
              <MarketingRouterMetricsField
                value={rule.significance_metrics}
                onChange={(metrics) => setRule((current) => ({ ...current, significance_metrics: metrics }))}
              />
            </Stack>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h2">Variants</Typography>
                <Button type="button" variant="text" onClick={addVariant}>
                  Add variant
                </Button>
              </Stack>
              {hasInvalidVariantTotal ? (
                <Alert severity="error">Variant weights must total 100%. Current total: {variantsTotal}%.</Alert>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Weights total {variantsTotal}%.
                </Typography>
              )}
              {rule.variants.map((variant, index) => (
                <MarketingRouterVariantCard
                  key={`${variant.variant_id}-${index}`}
                  variant={variant}
                  index={index}
                  canRemove={rule.variants.length > 1}
                  showFieldDescriptions={showFieldDescriptions}
                  onFieldChange={(field, value) => updateVariant(index, field, value)}
                  onMakeControl={() => setControlVariant(index)}
                  onRemove={() => removeVariant(index)}
                  onAddTreatment={() => addTreatment(index)}
                  onTreatmentTypeChange={(treatmentIndex, type) => updateTreatmentType(index, treatmentIndex, type)}
                  onTreatmentFieldChange={(treatmentIndex, field, value) =>
                    updateTreatmentField(index, treatmentIndex, field, value)
                  }
                  onRemoveTreatment={(treatmentIndex) => removeTreatment(index, treatmentIndex)}
                />
              ))}
            </Stack>
            {currentRuleErrors.length ? (
              <Alert severity="error" variant="outlined">
                <Stack spacing={1}>
                  <Typography variant="body2">Fix the following before saving:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {currentRuleErrors.map((error) => (
                      <Box component="li" key={error}>
                        {error}
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Alert>
            ) : null}
            {overlapGateMessage ? (
              <Typography color="text.secondary" variant="body2">
                {overlapGateMessage}
              </Typography>
            ) : null}
            {hasUnsavedChanges ? (
              <Box>
                <Typography color="warning.main" variant="body2">
                  {isEditing ? 'Unsaved changes:' : 'This rule has not been created yet.'}
                </Typography>
                {ruleChanges.length ? (
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                    {ruleChanges.map((change) => (
                      <Box component="li" key={change}>
                        <Typography variant="body2" color="text.secondary">
                          {change}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No changes since this rule was last saved.
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSaving || !currentRuleCheck.canSave || !overlapGate.canSave || !hasUnsavedChanges}
              >
                {isEditing ? 'Update rule' : 'Create rule'}
              </Button>
              <Button component={Link} to="/marketing/marketing-router-rules" variant="text">
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </>
  );
}
